import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { Briefcase, Star, Clock } from 'lucide-react';
import { Card } from './Card';
import { Button } from './Button';
import { getB2bReviews } from '../services/reviewsApi';
import type { B2bReview } from '../services/reviewsApi';

/**
 * Projectevaluaties (Review Engine, B2B): HR/directie beoordeelt de interne
 * professional na elke afgeronde projectmijlpaal. Open uitnodigingen staan
 * bovenaan met een directe link naar de wizard; daaronder de eigen, ingevulde
 * evaluaties met de score per vraag.
 */

const datum = (iso: string | null) =>
  iso ? new Date(iso).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const Sterren: React.FC<{ waarde: number | null }> = ({ waarde }) => (
  <span className="inline-flex items-center gap-0.5" aria-label={waarde != null ? `${waarde} van 5 sterren` : 'Geen score'}>
    {[1, 2, 3, 4, 5].map((v) => (
      <Star key={v} className={`w-4 h-4 ${(waarde ?? 0) >= v ? 'fill-amber-400 text-amber-400' : 'text-gray-200'}`} aria-hidden="true" />
    ))}
    <span className="ml-1 text-sm text-gray-600">{waarde != null ? waarde.toFixed(1) : '–'}</span>
  </span>
);

const mijlpaalLabel = (r: B2bReview) =>
  [r.project_code, r.milestone_volgnummer != null ? `Mijlpaal ${r.milestone_volgnummer}` : null, r.milestone_titel]
    .filter(Boolean)
    .join(' · ') || r.project_titel || 'Projectmijlpaal';

export const B2bReviewsPanel: React.FC = () => {
  const navigate = useNavigate();
  const { data, isLoading, isError } = useQuery({ queryKey: ['employer-reviews-b2b'], queryFn: getB2bReviews });

  if (isLoading) return null;
  if (isError) {
    return (
      <Card className="p-4 mb-6">
        <p className="text-sm text-red-600" role="alert">De projectevaluaties konden niet worden geladen.</p>
      </Card>
    );
  }
  const open = data?.open ?? [];
  const ingevuld = data?.ingevuld ?? [];
  if (open.length === 0 && ingevuld.length === 0) return null;

  return (
    <section className="mb-8" aria-labelledby="b2b-reviews-titel">
      <h2 id="b2b-reviews-titel" className="text-lg font-semibold text-gray-900 flex items-center gap-2">
        <Briefcase className="w-5 h-5 text-[#0B1F3A]" aria-hidden="true" />
        Projectevaluaties (B2B)
      </h2>
      <p className="text-sm text-gray-600 mb-4">
        Uw beoordeling van interne professionals na elke afgeronde projectmijlpaal: vakkennis, snelheid &amp;
        bereikbaarheid en doelgerichtheid binnen hun discipline.
      </p>

      {open.length > 0 && (
        <div className="space-y-2 mb-4">
          {open.map((r) => (
            <Card key={r.id} className="p-4 border-amber-200">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{r.professional_naam}</p>
                  <p className="text-xs text-gray-500">
                    {r.discipline_label ? `${r.discipline_label} · ` : ''}
                    {mijlpaalLabel(r)}
                  </p>
                  {r.verloopt_op && (
                    <p className="text-xs text-amber-700 mt-1 flex items-center gap-1">
                      <Clock className="w-3.5 h-3.5" aria-hidden="true" />
                      Open tot {datum(r.verloopt_op)}
                    </p>
                  )}
                </div>
                {r.review_url && (
                  <Button size="sm" onClick={() => navigate(r.review_url!)}>
                    Beoordeel (45 sec.)
                  </Button>
                )}
              </div>
            </Card>
          ))}
        </div>
      )}

      {ingevuld.length > 0 && (
        <div className="space-y-3">
          {ingevuld.map((r) => (
            <Card key={r.id} className="p-4">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="font-medium text-gray-900">{r.professional_naam}</p>
                  <p className="text-xs text-gray-500">
                    {r.discipline_label ? `${r.discipline_label} · ` : ''}
                    {mijlpaalLabel(r)} · {datum(r.submitted_at)}
                  </p>
                </div>
                <Sterren waarde={r.average_rating} />
              </div>
              <dl className="mt-3 grid gap-2 sm:grid-cols-3">
                {r.questions.map((q) => (
                  <div key={q.id} className="rounded-md bg-gray-50 p-2">
                    <dt className="text-xs font-medium text-gray-600" title={q.question}>
                      {q.label}
                    </dt>
                    <dd className="text-sm text-gray-900">{r[q.id] != null ? `${r[q.id]} / 5` : '–'}</dd>
                  </div>
                ))}
              </dl>
              {r.open_comment && <p className="mt-2 text-sm text-gray-700 italic">&ldquo;{r.open_comment}&rdquo;</p>}
            </Card>
          ))}
        </div>
      )}
    </section>
  );
};

export default B2bReviewsPanel;
